// Client telemetry: app_open, page_view, frontend_error, notification_click.
// Batched up to 20 events or 30s, whichever first. Flush on visibility hidden.
// Drops batch silently on any failure (telemetry never blocks UX).
//
// React strict-mode guard via useRef so dev double-mount doesn't double-emit.
// session_id: crypto.randomUUID() in sessionStorage (tab-scoped, persists reload).

import { useEffect, useRef } from 'react';
import * as api from './api';

const MAX_BATCH = 20;
const FLUSH_INTERVAL_MS = 30_000;
const STORAGE_KEY = 'vava_session_id';

let buffer: api.ClientEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function getSessionId(): string {
  let id = sessionStorage.getItem(STORAGE_KEY);
  if (!id) {
    // crypto.randomUUID() is on all modern browsers; fall back for ancient ones
    id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem(STORAGE_KEY, id);
  }
  return id;
}

function detectDevice(): 'mobile' | 'desktop' {
  return window.innerWidth < 768 ? 'mobile' : 'desktop';
}

function detectPwa(): 0 | 1 {
  try {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return 1;
    // iOS Safari non-standard. Without this, iOS PWA installs read as "browser".
    if ((navigator as Navigator & { standalone?: boolean }).standalone === true) return 1;
  } catch { /* swallow */ }
  return 0;
}

async function flush(): Promise<void> {
  if (buffer.length === 0) return;
  const batch = buffer.splice(0, MAX_BATCH);
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  try {
    await api.postEvents(batch);
  } catch {
    // Drop. Telemetry must never break UX. No retry loop.
  }
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => { flushTimer = null; void flush(); }, FLUSH_INTERVAL_MS);
}

export function track(kind: api.ClientEvent['kind'], extra: Partial<api.ClientEvent> = {}): void {
  buffer.push({
    kind,
    session_id: getSessionId(),
    device: detectDevice(),
    pwa: detectPwa(),
    path: window.location.hash.replace(/^#/, '') || '/',
    ...extra
  });
  if (buffer.length >= MAX_BATCH) {
    void flush();
  } else {
    scheduleFlush();
  }
}

// Last-emitted hash, to dedupe rapid hashchange events (e.g. programmatic
// navigate then immediate replaceState). 100ms window.
let lastPathEmit = { path: '', at: 0 };

function trackPageView(): void {
  const path = window.location.hash.replace(/^#/, '') || '/';
  const now = Date.now();
  if (path === lastPathEmit.path && now - lastPathEmit.at < 100) return;
  lastPathEmit = { path, at: now };
  track('page_view', { path });
}

// Recursive-error guard. If our error handler itself throws, we'd loop forever.
let inErrorHandler = false;

function captureError(msg: string, stack?: string): void {
  if (inErrorHandler) return;
  inErrorHandler = true;
  try {
    track('frontend_error', {
      error_msg: msg.slice(0, 500),
      error_stack: stack ? stack.slice(0, 2048) : null
    });
  } catch { /* swallow */ }
  inErrorHandler = false;
}

/**
 * Mount once at app root. React strict-mode safe via useRef guard.
 * Wires app_open + page_view + window.onerror + unhandledrejection +
 * visibilitychange flush.
 */
export function useTelemetry(authed: boolean): void {
  const initialized = useRef(false);
  useEffect(() => {
    if (!authed) return;          // Don't track for unauthed users (SPA never shows pre-login anyway)
    if (initialized.current) return;
    initialized.current = true;

    // app_open
    track('app_open');

    // page_view on hash change
    const onHash = () => trackPageView();
    window.addEventListener('hashchange', onHash);

    // Error capture
    const onError = (e: ErrorEvent) => {
      captureError(e.message || 'unknown error', e.error?.stack);
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      const reason = e.reason;
      const msg = reason instanceof Error ? reason.message : String(reason);
      const stack = reason instanceof Error ? reason.stack : undefined;
      captureError('unhandled rejection: ' + msg, stack);
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);

    // Flush on visibility hidden (tab close, switch, mobile lock)
    const onVis = () => {
      if (document.visibilityState === 'hidden' && buffer.length > 0) void flush();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      window.removeEventListener('hashchange', onHash);
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [authed]);
}
